import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from '../../api/insolita';
import { GalleryImage } from '../../api/types';
import { resolveImg } from '../../utils/img';

const KNOWN_CATEGORIES = ['gallery', 'sponsor', 'partner-institutional', 'partner-tech'];

const GalleryAdmin: React.FC = () => {
  const [items, setItems] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState('gallery');
  const [title, setTitle] = useState('');

  const load = () => {
    setLoading(true);
    adminApi.gallery.list().then(setItems).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const categories = useMemo(() => {
    const set = new Set<string>([...KNOWN_CATEGORIES, ...items.map((i) => i.category)]);
    return Array.from(set);
  }, [items]);

  const grouped = useMemo(() => {
    const map: Record<string, GalleryImage[]> = {};
    items.forEach((i) => { (map[i.category] = map[i.category] || []).push(i); });
    return map;
  }, [items]);

  const upload = async (file: File) => {
    setUploading(true);
    try {
      await adminApi.gallery.upload(file, category, title);
      setTitle('');
      load();
    } finally { setUploading(false); }
  };

  const update = async (id: number, body: Partial<GalleryImage>) => {
    await adminApi.gallery.update(id, body);
    load();
  };

  const remove = async (id: number) => {
    if (!window.confirm('Eliminare questa immagine?')) return;
    await adminApi.gallery.remove(id);
    load();
  };

  if (loading) return <p className="text-neutral-500">Caricamento…</p>;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-1">Galleria & Sponsor</h1>
      <p className="text-neutral-500 mb-6 text-sm">Carica, riordina, sostituisci o rimuovi foto e loghi. Le categorie <code>sponsor</code>, <code>partner-institutional</code> e <code>partner-tech</code> alimentano la pagina Rally Raid.</p>

      <div className="bg-white rounded-lg border border-neutral-200 p-4 mb-8 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Categoria</label>
          <input list="cats" value={category} onChange={(e) => setCategory(e.target.value)}
            className="border border-neutral-200 rounded px-3 py-2 text-sm" />
          <datalist id="cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Titolo (opzionale)</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)}
            className="w-full border border-neutral-200 rounded px-3 py-2 text-sm" />
        </div>
        <label className="px-4 py-2 bg-[#E4FF1A] text-[#0B0B0C] font-medium rounded cursor-pointer text-sm">
          {uploading ? 'Caricamento…' : '+ Carica immagine'}
          <input type="file" accept="image/*" hidden disabled={uploading}
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        </label>
      </div>

      {Object.entries(grouped).map(([cat, list]) => (
        <section key={cat} className="mb-8">
          <h2 className="text-xs uppercase tracking-widest text-neutral-400 mb-3 border-b pb-2">{cat} · {list.length}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {list.map((g) => (
              <div key={g.id} className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
                <div className="aspect-video bg-neutral-50 flex items-center justify-center">
                  <img src={resolveImg(g.url)} alt={g.altText || g.title || ''} className="max-h-full max-w-full object-contain" />
                </div>
                <div className="p-3 space-y-2">
                  <input defaultValue={g.title || ''} placeholder="Titolo"
                    onBlur={(e) => e.target.value !== (g.title || '') && update(g.id, { title: e.target.value })}
                    className="w-full text-sm border border-neutral-200 rounded px-2 py-1" />
                  <div className="flex items-center gap-2">
                    <input defaultValue={g.category}
                      onBlur={(e) => e.target.value !== g.category && update(g.id, { category: e.target.value })}
                      className="flex-1 text-xs border border-neutral-200 rounded px-2 py-1" />
                    <input type="number" defaultValue={g.sortOrder} title="Ordine"
                      onBlur={(e) => Number(e.target.value) !== g.sortOrder && update(g.id, { sortOrder: Number(e.target.value) })}
                      className="w-14 text-xs border border-neutral-200 rounded px-2 py-1" />
                  </div>
                  <div className="flex justify-between items-center">
                    <label className="text-xs flex items-center gap-1">
                      <input type="checkbox" checked={g.active} onChange={(e) => update(g.id, { active: e.target.checked })} />
                      Attiva
                    </label>
                    <button onClick={() => remove(g.id)} className="text-xs text-red-500 hover:underline">Elimina</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default GalleryAdmin;
